import * as plugins from './plugins.js';
import { type CSSResult } from 'lit';

export class DeesElement extends plugins.lit.LitElement {
  // STATIC — component-level constraint helper (use in `static styles = [...]`)

  private static getContainerName(): string {
    return (this as any).is || this.name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  static cssForConstraint(constraints: { maxWidth?: number; minWidth?: number }) {
    return (cssArg: CSSResult) =>
      plugins.domtools.breakpoints.cssForConstraintContainer(constraints, this.getContainerName())(cssArg);
  }

  // INSTANCE
  /**
   * Reflected as the `gobright` attribute so components can style themes
   * directly via `:host([gobright]) { ... }` without per-value bdTheme pairs.
   */
  @plugins.lit.property({ type: Boolean, reflect: true })
  public accessor goBright: boolean = false;

  // domtools
  public domtoolsPromise: Promise<plugins.domtools.DomTools>;

  @plugins.lit.property()
  public accessor domtools!: plugins.domtools.DomTools;

  public rxSubscriptions: plugins.smartrx.rxjs.Subscription[] = [];
  private themeSubscription!: plugins.smartrx.rxjs.Subscription;

  private elementDomReadyDeferred = plugins.domtools.plugins.smartpromise.defer();
  public elementDomReady = this.elementDomReadyDeferred.promise;

  constructor(optionsArg: plugins.domtools.IDomToolsContructorOptions = {}) {
    super();
    this.domtoolsPromise = plugins.domtools.elementBasic.setup(this, optionsArg);
    this.domtoolsPromise.then((domtoolsArg) => {
      this.domtools = domtoolsArg;
    });
  }

  /**
   * Called when the theme changes between bright and dark.
   * Override this method to handle theme changes.
   * @param goBright - true if switching to bright theme, false if switching to dark theme
   */
  protected themeChanged?(goBright: boolean): void;

  public async connectedCallback() {
    super.connectedCallback();
    const domtools = await this.domtoolsPromise;
    this.themeSubscription = domtools.themeManager.themeObservable.subscribe((goBrightArg) => {
      this.goBright = goBrightArg;
      // Call themeChanged if it's defined
      if (this.themeChanged) {
        this.themeChanged(goBrightArg);
      }
    });
    this.rxSubscriptions.push(this.themeSubscription);
    for (const startupFunction of this.startupFunctions) {
      await startupFunction();
    }
    this.dispatchEvent(new CustomEvent('deesElementConnected'));
  }

  public firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.firstUpdated(_changedProperties);
    this.elementDomReadyDeferred.resolve();
  }

  private startupFunctions: (() => void | Promise<any>)[] = [];
  public registerStartupFunction(startupFunctionArg: () => void) {
    this.startupFunctions.push(startupFunctionArg);
  }

  private garbageFunctions: (() => void | Promise<any>)[] = [];
  public registerGarbageFunction(garbageFunctionArg: () => void) {
    this.garbageFunctions.push(garbageFunctionArg);
  }

  public async disconnectedCallback() {
    await this.domtoolsPromise;
    super.disconnectedCallback();
    for (const subscription of this.rxSubscriptions) {
      subscription.unsubscribe();
    }
    for (const garbageFunction of this.garbageFunctions) {
      await garbageFunction();
    }
    this.dispatchEvent(new CustomEvent('deesElementDisconnected'));
  }
}
